'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase-browser';

type SupabaseContextType = {
  supabase: ReturnType<typeof createClient>;
  user: User | null;
  session: Session | null;
  authReady: boolean;
};

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export function SupabaseProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    let isActive = true;

    // Suppress the known Supabase 'steal' AbortError in dev mode.
    // This happens because multiple components might try to refresh the session
    // at the exact same time (especially with React Strict Mode rendering twice),
    // causing Supabase to break its own lock to prevent a deadlock.
    // It is completely harmless.
    const originalConsoleError = console.error;
    if (process.env.NODE_ENV === 'development') {
      console.error = (...args: any[]) => {
        if (
          args[0] &&
          typeof args[0] === 'string' &&
          args[0].includes("Lock broken by another request with the 'steal' option")
        ) {
          return; // Suppress
        }
        if (args[0] && args[0].name === 'AbortError' && args[0].message?.includes('steal')) {
          return; // Suppress
        }
        originalConsoleError.apply(console, args);
      };
    }

    const loadUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (!isActive) return;
        setSession(session);
        setUser(session?.user ?? null);
      } catch (e: any) {
        // We catch here just in case the error throws instead of returning
        if (e.name !== 'AbortError') {
          console.error('Supabase getSession error:', e);
        }
      } finally {
        if (isActive) setAuthReady(true);
      }
    };

    loadUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!isActive) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setAuthReady(true);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
      if (process.env.NODE_ENV === 'development') {
        console.error = originalConsoleError; // Restore
      }
    };
  }, [supabase]);

  return (
    <SupabaseContext.Provider value={{ supabase, user, session, authReady }}>
      {children}
    </SupabaseContext.Provider>
  );
}

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used inside a SupabaseProvider');
  }
  return context;
};
